import {
  colorBackgroundAlt,
  colorContentOnBrandDefault,
  colorStatusNeutralBoldest,
  colorStatusNeutralDefault,
} from '@duxton-web/design-tokens/themes/light';
import { Button, Card, Flex, Typography } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Image imports for campaign status icons
import inactiveCalendar from 'img/inactive_calendar.svg';
import pendingApprovalIcon from 'img/ucd_campaign_pending_approval.svg';

import {
  UPDATE_UCD_CAMPAIGN,
  UPDATE_UCD_CAMPAIGN_STATUS,
  getUCDCampaign,
  getUCDProject,
  updateUCDCampaignStatus,
} from 'omega-rtc/actions/ucd';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import SmartCampaignOverviewCard from 'omega-rtc/components/form/ucd/SmartCampaignOverviewCard';
import { showControlGroup } from 'omega-rtc/config';

import ScribeEventName from 'omega-rtc/constants/scribe';
import {
  SMART_CAMPAIGN_STATUS_UPDATE_TYPES as ACTION_TYPE,
  HUMAN_READ_DATE_TIME_FORMAT,
  HUMAN_READ_DATE_FORMAT,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { SmartCampaignStatus, UcdProjectResourceName, UcdUniversalScope } from 'omega-rtc/data/trident-campaigns';
import {
  isRequestsPending,
  isRequestsSuccessful,
} from 'omega-rtc/utils/request-state-utils';
import { processCampaignData } from 'omega-rtc/utils/scribe';
import {
  getActionTypeAndURLFromMetaDataPayload,
  getCreatorStatusTrackerCardContent,
  mapAttributesValuesToLabelsInMystiqueResponse,
  normalizeSmartCommsLanguageCode,
} from 'omega-rtc/utils/smart-campaign-utils';
import {
  isTaiwanCampaignApprovalAllowed,
  TAIWAN_CAMPAIGN_APPROVAL_RESTRICTED_MESSAGE,
} from 'omega-rtc/utils/taiwan-campaign-operation';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import { getValueByFeatureFlag, hasPermission } from 'utils/user-utils';

dayjs.extend(timezone);

const SMART_CAMPAIGN_MESSAGE = 'smartCampaignMessage';

// On rejecting a campaign, we need to pass status as 'REJECTED' and backend will change status of smart-campaign to 'DRAFT'.
const SMART_CAMPAIGN_REJECTED = 'REJECTED';

// Show snackBar instead of toastr, on error from API Middleware


type SmartCampaignOverviewProps = {
  actionType: string;
  setActionType: (actionType: string | undefined) => void;
  router: any;
};

const SmartCampaignOverview = ({ actionType, setActionType, router }: SmartCampaignOverviewProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.app.user);
  const request = useSelector((state: any) => state.requests);
  const campaignView = useSelector((state: any) => state.ucd.campaignView);
  const projectView = useSelector((state: any) => state.ucd.projectView);
  const featureFlagConfig = useSelector((state: any) => state.newTridents.featureFlagConfig);
  const isHolosPermissionEnabled = _.get(featureFlagConfig, 'holosPortalPermissionEnbaled', false); // TODO: remove this after holos portal permission is enabled for all users
  const countries = useSelector((state: any) => state.countries);

  const {
    campaign,
    createdBy: campaignCreatedBy,
    countryID,
    projectID,
    campaignID,
    status: campaignStatus,
  } = campaignView;
  const effectiveCountryID = countryID || _.get(projectView, 'project.countryID');

  const formattedStartDate = campaign && campaign.start ? dayjs(campaign.start).subtract(1, 'day').format('MMMM DD, YYYY') : '';
  const approverStatusCardContent = {
    [SmartCampaignStatus.pending]: {
      image: pendingApprovalIcon,
      title: 'This campaign is pending for your approval',
      description: `Please review and take action by 23:59 on ${formattedStartDate}. The creator will be notified once you approve or reject the campaign.`,
    },
  };

  const channel = useMemo(
    () => campaign && campaign.channels && campaign.channels[0], // Assumed to have only one channel
    [campaign],
  );

  const country = countries.entities.find(
    (entity: any) => entity.countryID === effectiveCountryID,
  );

  const toastMessageBasedOnActionType = useMemo(() => {
    return {
      [ACTION_TYPE.APPROVE]: `You approved ${campaign.name} successfully. We'll notify the campaign creator.`,
      [ACTION_TYPE.REJECT]: `You rejected ${campaign.name}. We'll notify the campaign creator.`,
      [ACTION_TYPE.REQUEST_FOR_WITHDRAWAL]:
        'Campaign has been successfully withdrawn',
      [ACTION_TYPE.WITHDRAW_UPCOMING]:
        'Campaign has been successfully withdrawn',
      [ACTION_TYPE.DEACTIVATE]: 'Campaign has been deactivated successfully',
      [ACTION_TYPE.REQUEST_FOR_APPROVAL]: `You submitted ${campaign.name} successfully.`,
    };
  }, [campaignView]);

  const getApproverName = useMemo(() => {
    let approverName = '';
    if (campaign && campaign.approver) {
      approverName = campaign.approver
        .match(/^([^@]*)@/)[1]
        .replace(/\./g, ' ')
        .replace(/(^\w|\s\w)/g, (firstLetterOfName: string) =>
          firstLetterOfName.toUpperCase(),
        );
    }

    return approverName;
  }, [campaign]);

  const creatorStatusTrackerCardContent = useMemo(() => {
    return getCreatorStatusTrackerCardContent(campaignView.status, {
      approverName: getApproverName,
      startDate: campaign.start,
    });
  }, [campaignView.status, getApproverName, campaign]);

  const {
    isCreator,
    showWithdrawButton,
    showCreatorStatusTrackerCard,
    showApproveAndRejectButton,
    showApproverStatusTrackerCard,
    isContinueSetupButtonEnabled,
  } = useMemo(() => {
    const isCreator = user.email === campaignView.createdBy;
    const isApprover = user.email === campaignView.campaign.approver;
    const isTaiwanApprovalAllowed = isTaiwanCampaignApprovalAllowed(
      effectiveCountryID,
      user.email,
    );

    const showWithdrawButton =
      campaignView.status === SmartCampaignStatus.pending && isCreator;
    const showCreatorStatusTrackerCard =
      creatorStatusTrackerCardContent && isCreator;

    /* campaign with status 'PENDING' should show the card with approve and
     * reject buttons. - For Approver View
     */
    // TODO: Use UcdCampaignResourceName instead of UcdProjectResourceName once the permission is updated in production
    // Reason: UcdCampaignResourceName-active is not a part of the permission list yet, so we are using UcdProjectResourceName-active instead to unblock the feature
    const hasActivePermission = hasPermission(user.userPermissions, UcdProjectResourceName, UcdUniversalScope, 'active');
    const showApproveAndRejectButton =
      campaignView.status === SmartCampaignStatus.pending
      && isApprover
      && isTaiwanApprovalAllowed;
    const showApproverStatusTrackerCard =
      approverStatusCardContent[campaignView.status] && isApprover && getValueByFeatureFlag(isHolosPermissionEnabled, hasActivePermission, true);
    const isContinueSetupButtonEnabled =
      campaignView.status === SmartCampaignStatus.draft && isCreator;

    return {
      isCreator,
      showWithdrawButton,
      showCreatorStatusTrackerCard,
      showApproveAndRejectButton,
      showApproverStatusTrackerCard,
      isContinueSetupButtonEnabled,
    };
  }, [user, campaignView, effectiveCountryID, isHolosPermissionEnabled]);
  const campaignUpdateStatusReq = useMemo(
    () => _.get(request, getRequestStateKey(UPDATE_UCD_CAMPAIGN_STATUS), {}),
    [request],
  );
  const isCampaignUpdateStatusPending = useMemo(
    () => isRequestsPending(campaignUpdateStatusReq),
    [campaignUpdateStatusReq],
  );

  const updateCampaignReq = useMemo(
    () => _.get(request, getRequestStateKey(UPDATE_UCD_CAMPAIGN)),
    [request],
  );
  const isUpdateCampaignPending = useMemo(
    () => isRequestsPending(updateCampaignReq),
    [updateCampaignReq],
  );

  useEffect(() => {
    // UseEffect to show toast message after success of campaign update.
    const message = JSON.parse(sessionStorage.getItem(SMART_CAMPAIGN_MESSAGE) || '{}');
    if (!message || !message.content) {
      return;
    }

    const snackbarViewType = {
      [ACTION_TYPE.REJECT]: snackBar.neutral,
    };
    const snackbarBasedOnType = snackbarViewType[message.type];
    const snackbarContent = message.content;

    if (snackbarBasedOnType) {
      snackbarBasedOnType({ content: snackbarContent });
    } else {
      snackBar.success({ content: snackbarContent });
    }

    sessionStorage.removeItem(SMART_CAMPAIGN_MESSAGE);
  }, []);

  useEffect(() => {
    const messageBasedOnAction = toastMessageBasedOnActionType[actionType];
    if (isRequestsSuccessful(campaignUpdateStatusReq) && messageBasedOnAction) {
      const snackbarViewType = {
        [ACTION_TYPE.REJECT]: 'neutral',
      };
      const snackbarBasedOnType = snackbarViewType[actionType];

      snackBar[snackbarBasedOnType || 'success']({
        content: messageBasedOnAction,
      });
      setActionType(undefined);
      dispatch(getUCDCampaign(projectID, campaignID));
    }
  }, [campaignUpdateStatusReq]);

  useEffect(() => {
    const doesProjectExistInStore =
      projectView && projectView.projectID === projectID;

    if (projectID && !doesProjectExistInStore) {
      dispatch(getUCDProject(projectID));
    }
  }, [projectID]);

  useEffect(() => {
    if (
      isRequestsSuccessful(updateCampaignReq) &&
      actionType === ACTION_TYPE.REQUEST_FOR_APPROVAL
    ) {
      dispatch(
        updateUCDCampaignStatus(
          SmartCampaignStatus.pending,
          user.email,
          projectID,
          campaignID,
        ),
      );
      scribe.sendEvent(
        ScribeEventName.CAMPAIGN_REQUESTED,
        processCampaignData({ campaignView, user }),
      );
    }
  }, [updateCampaignReq]);

  const updateCampaignStatus = (status: string) => {
    dispatch(
      updateUCDCampaignStatus(
        status,
        user.email,
        projectID,
        campaignID,
      ),
    );
  };

  const handleApprove = () => {
    if (!isTaiwanCampaignApprovalAllowed(effectiveCountryID, user.email)) {
      snackBar.error({ content: TAIWAN_CAMPAIGN_APPROVAL_RESTRICTED_MESSAGE });
      return;
    }

    // ucd turns an approval sent as 'PENDING-AIP' into 'ACTIVE'.
    updateCampaignStatus(SmartCampaignStatus.pendingAIP);
    setActionType(ACTION_TYPE.APPROVE);
    scribe.sendEvent(
      ScribeEventName.CAMPAIGN_APPROVED,
      processCampaignData({ campaignView, user }),
    );
  };

  const handleReject = () => {
    updateCampaignStatus(SMART_CAMPAIGN_REJECTED);
    setActionType(ACTION_TYPE.REJECT);
    scribe.sendEvent(
      ScribeEventName.CAMPAIGN_REJECTED,
      processCampaignData({ campaignView, user }),
    );
  };

  const handleWithdrawCampaign = () => {
    updateCampaignStatus(SmartCampaignStatus.draft);
    setActionType(ACTION_TYPE.REQUEST_FOR_WITHDRAWAL);
    scribe.sendEvent(
      ScribeEventName.CAMPAIGN_WITHDRAWN,
      processCampaignData({ campaignView, user })
    );
  };

  const handleEdit = () => {
    router.push(UCD_ROUTES_LINK.SMART_CAMPAIGN_EDIT(campaignView.projectID, campaignView.campaignID));
  };

  const contentData = useMemo(() => {
    const defaultReturn = {
      selectedLanguages: [],
      metaDataForEachLanguages: {},
      isManualMode: false,
      primaryLanguage: '',
      actionType: '',
      actionURL: '',
      pushNotificationImage: '',
    };

    if (
      !campaignView ||
      !campaignView.campaign ||
      !campaignView.campaign.smartCommsContentMetadata ||
      !campaignView.campaign.smartCommsContentMetadata.contents
    ) {
      return defaultReturn;
    }

    const contentsBasedOnChannel =
      campaignView.campaign.smartCommsContentMetadata.contents[0];
    if (!contentsBasedOnChannel || !contentsBasedOnChannel.rounds) {
      return defaultReturn;
    }
    const primaryLanguage = normalizeSmartCommsLanguageCode(
      campaignView.campaign.smartCommsContentMetadata.primaryLanguage,
    );

    const contentBasedOnLanguageAndMetaData =
      getContentVariantsForEachLanguages(
        contentsBasedOnChannel.rounds,
        primaryLanguage,
      );
    const isManualMode = Boolean(
      campaignView.campaign.smartCommsContentMetadata.isManuallyEnteredContent,
    );
    return {
      primaryLanguage,
      ...contentBasedOnLanguageAndMetaData,
      isManualMode,
    };
  }, [campaignView]);

  const controlGroupTriggerTime = useMemo(() => {
    if (
      campaignView &&
      campaignView.campaign &&
      campaignView.campaign.controlGroupTime &&
      country &&
      country.timezones
    ) {
      return dayjs
        .tz(
          dayjs(campaignView.campaign.controlGroupTime),
          country.timezones[0].tz,
        )
        .format(HUMAN_READ_DATE_TIME_FORMAT);
    }
    return '';
  }, [campaignView]);

  const isStartDatePassed = dayjs(campaign.start).isBefore(dayjs().add(0, 'days')) && campaign.isPendingApproval;

  return campaignView ? (
    <Flex
      vertical
      align="center"
      gap={24}
      style={{
        padding: '40px',
        background: colorBackgroundAlt,
      }}
    >
      <Flex vertical justify="flex-start" gap={24}>

        {/* Approver Status Tracker Card */}
        {isStartDatePassed && showApproverStatusTrackerCard && (
          <StatusTrackerCard
            title="Campaign not approved in time"
            description="Approval time has passed. The creator has been informed to update the period and submit again. Please remember to approve the campaign by 23:59 the day before the campaign period."
            image={inactiveCalendar}
          />
        )}

        {!isStartDatePassed && showApproverStatusTrackerCard && (
          <StatusTrackerCard
            title={approverStatusCardContent[campaignView.status].title}
            description={
              approverStatusCardContent[campaignView.status].description
            }
            image={approverStatusCardContent[campaignView.status].image}
          >
            {showApproveAndRejectButton && (
              <Flex align="center" gap={12}>
                <Button
                  type="primary"
                  loading={
                    isCampaignUpdateStatusPending &&
                    actionType === ACTION_TYPE.APPROVE
                  }
                  onClick={handleApprove}
                >
                  Approve
                </Button>
                <Button
                  type="primary"
                  loading={
                    isCampaignUpdateStatusPending &&
                    actionType === ACTION_TYPE.REJECT
                  }
                  danger
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </Flex>
            )}
          </StatusTrackerCard>
        )}

        {/* Creator Status Tracker Card */}
        {isStartDatePassed && showCreatorStatusTrackerCard && (
          <StatusTrackerCard
            title={'Update campaign period and submit again'}
            description={'This campaign was not approved in time. Please adjust the period and resubmit it for approval. We will withdraw the previous approval request if you edit the campaign.'}
            image={inactiveCalendar}
          >
            <Button
              type="primary" onClick={() => {
                updateCampaignStatus(SmartCampaignStatus.draft);
                setTimeout(() => {
                  window.location.href = UCD_ROUTES_LINK.SMART_CAMPAIGN_EDIT(campaignView.projectID, campaignView.campaignID);
                }, 1000);
              }}
            >
                  Edit campaign
            </Button>

          </StatusTrackerCard>
        )}

        {!isStartDatePassed && showCreatorStatusTrackerCard && (
          <StatusTrackerCard
            title={creatorStatusTrackerCardContent.title}
            description={creatorStatusTrackerCardContent.description}
            image={creatorStatusTrackerCardContent.image}
          >
            {showWithdrawButton && (
              <Button
                type="text"
                style={{ width: 'fit-content' }}
                loading={
                  isCampaignUpdateStatusPending &&
                  actionType === ACTION_TYPE.REQUEST_FOR_WITHDRAWAL
                }
              >
                <Typography.Body
                  level={3}
                  strong
                  style={{ color: colorStatusNeutralBoldest }}
                  onClick={handleWithdrawCampaign}
                >
                  Withdraw campaign
                </Typography.Body>
              </Button>
            )}
            {isContinueSetupButtonEnabled && (
              <Button
                type="primary"
                style={{ width: 'fit-content' }}
                loading={
                  (isCampaignUpdateStatusPending || isUpdateCampaignPending) &&
                  actionType === ACTION_TYPE.REQUEST_FOR_APPROVAL
                }
              >
                <Typography.Body
                  level={3}
                  strong
                  style={{ color: colorContentOnBrandDefault }}
                  onClick={handleEdit}
                >
                  Edit campaign
                </Typography.Body>
              </Button>
            )}
          </StatusTrackerCard>
        )}
      </Flex>

      <SmartCampaignOverviewCard
        channelType={channel}
        errorReason={isCreator && campaign.errorReason}
        campaignName={campaign.name}
        projectID={projectID}
        campaignStatus={campaignStatus}
        startDate={campaign.start}
        endDate={campaign.end}
        hoursInUTC={campaign.hoursInUTC}
        isMetricsAvailable={campaign.isMetricsAvailable}
        vertical={campaign.verticals && campaign.verticals[0]}
        campaignCode={campaign.code}
        campaignCreatedBy={campaignCreatedBy}
        isManualMode={contentData.isManualMode}
        primaryLanguage={contentData.primaryLanguage}
        actionType={contentData.actionType}
        actionURL={contentData.actionURL}
        metaDataForEachLanguages={contentData.metaDataForEachLanguages}
        selectedLanguages={contentData.selectedLanguages}
        segment={campaign.targetSegments && campaign.targetSegments.join(', ')}
        segmentType={campaign.segmentType}
        {...(showControlGroup ? { controlGroupTime: controlGroupTriggerTime } : {})}
        pushNotificationImage={contentData.pushNotificationImage}
        cardTitle="Campaign Overview"
        cardStyle={{
          width: 844,
        }}
        isRecurrenceEnabled={campaign.smartCommsMetadata && campaign.smartCommsMetadata.isRecurrenceEnabled}
        recurrenceWindowDays={campaign.smartCommsMetadata && campaign.smartCommsMetadata.customRecurrenceWindowInDays}
        nextSubmissionTime={campaign.smartCommsMetadata && campaign.smartCommsMetadata.nextSubmissionTime ? dayjs(campaign.smartCommsMetadata.nextSubmissionTime).format(HUMAN_READ_DATE_FORMAT) : ''}
        audienceType={campaign.smartCommsContentMetadata && campaign.smartCommsContentMetadata.audienceType}
      />
    </Flex>
  ) : (
    <div>Campaign not found</div>
  );
};

type StatusTrackerCardProps = {
  title: string;
  description: string;
  image: string;
  children?: React.ReactNode;
};

const StatusTrackerCard = ({ title, description, image, children }: StatusTrackerCardProps) => {
  return (
    <Card
      style={{
        width: 844,
        height: 'fit-content',
      }}
    >
      <Flex align="center" gap={24} style={{ width: '100%' }}>
        {/* Image */}
        <div style={{ minWidth: 60, display: 'flex', justifyContent: 'center' }}>
          <img src={image} height={48} alt="" />
        </div>
        {/* Text */}
        <Flex vertical style={{ flex: 1 }}>
          <Typography.Body level={2} strong>
            {title}
          </Typography.Body>
          <Typography.Body
            level={3}
            style={{ color: colorStatusNeutralDefault }}
          >
            {description}
          </Typography.Body>
        </Flex>
        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          {children}
        </div>
      </Flex>
    </Card>
  );
};

/**
 *
 * @param  contentsInEachRound Rounds array from 'GET campaign' response.
 * @param  primaryLanguage
 * @returns Selected Only Variants for each languages
 *          eg:  {
 *                selectedLanguages: ['en', ...],
 *                metaDataForEachLanguages:  [languageName]:{contentVariants: [ {  variantNumber, title, message, isControlGroup}, ...], pciMetaData: {...}},
 *                actionType: 'deeplink',
 *                actionURL: ''
 *               }
 */
const getContentVariantsForEachLanguages = (
  contentsInEachRound: any[],
  primaryLanguage: string,
) => {
  const languages = new Set();
  let variantNumber = 0;
  const metaDataForEachLanguages: any = {};

  let actionType;
  let actionURL;
  let pushNotificationImage;

  contentsInEachRound.forEach((contentOfRound) => {
    if (!contentOfRound.variants) {
      return;
    }

    // Looping through variants
    contentOfRound.variants.forEach((variant: any) => {
      variantNumber += 1;

      if (!variant.languages) {
        return;
      }

      // Looping through languages
      variant.languages.forEach((variantOfALanguage: any) => {
        const currentLanguageCode = normalizeSmartCommsLanguageCode(
          variantOfALanguage.language,
        );
        languages.add(currentLanguageCode);

        if (!variant.isChosen && !variant.isControlGroup) {
          return;
        }
        const actionTypeAndUrl = getActionTypeAndURLFromMetaDataPayload(
          variantOfALanguage.payload,
        );
        actionType = actionTypeAndUrl.actionType;
        actionURL = actionTypeAndUrl.actionURL;

        pushNotificationImage = variantOfALanguage.payload.imageUrl;

        const titleAndMessage = {
          title: mapAttributesValuesToLabelsInMystiqueResponse(
            variantOfALanguage.payload.title ||
              variantOfALanguage.payload.push_title,
            false, // matchSingleBrackets to be false
          ),
          message: mapAttributesValuesToLabelsInMystiqueResponse(
            variantOfALanguage.payload.message ||
              variantOfALanguage.payload.push_message,
            false, // matchSingleBrackets to be false
          ),
        };
        const pciMetaData = {
          category: variantOfALanguage.payload.inbox_category || '',
          title: variantOfALanguage.payload.inbox_title || '',
          subtitle: variantOfALanguage.payload.inbox_subtitle || '',
          coverImageURL: variantOfALanguage.payload.inbox_cover_image || '',
          message: variantOfALanguage.payload.inbox_message_content || '',
          buttonText: variantOfALanguage.payload.inbox_button_text || '',
          buttonLink: variantOfALanguage.payload.inbox_button_link || '',
        };

        if (!metaDataForEachLanguages[currentLanguageCode]) {
          metaDataForEachLanguages[currentLanguageCode] = {};
        }
        const previousContentVariants =
          metaDataForEachLanguages[currentLanguageCode].contentVariants || [];

        metaDataForEachLanguages[currentLanguageCode] = {
          pciMetaData,
          contentVariants: [
            ...previousContentVariants,
            {
              ...variantOfALanguage.payload,
              ...titleAndMessage,
              variantNumber,
              isControlGroup: variant.isControlGroup,
              slang: variantOfALanguage.slang,
            },
          ],
        };
      });
    });
  });

  return {
    selectedLanguages: _.union([primaryLanguage], Array.from(languages)),
    metaDataForEachLanguages,
    actionType,
    actionURL,
    pushNotificationImage,
  };
};

export default SmartCampaignOverview;
